/**
 * Stream Deck action catalogue — the list the Inspector picker offers.
 *
 * Source: OPBridge `GET /streamdeck/actions`, which proxies Elgato's MCP
 * Deck (`@elgato/mcp-server` on HTTP :9090 → named pipe → Stream Deck app).
 * Only actions the operator placed in Stream Deck's **MCP Actions** profile
 * are exposed — that allowlist is theirs, not ours, which is exactly why
 * this is a fetched picker and not a typed-in id.
 *
 * Fetched on demand (picker open / explicit refresh), NOT polled. The
 * catalogue only changes when the operator edits their Stream Deck profile,
 * so a poll would be steady LAN + battery cost on a gig iPad for a list
 * that is static across a whole set.
 *
 * The status is deliberately four-valued rather than "list or empty".
 * "No actions configured" and "the MCP server isn't running" are different
 * problems with different fixes, and collapsing them into an empty list is
 * how the retired stub client misled everyone for a year — it reported
 * success while doing nothing. Unavailable must never read as fine.
 */

import { createSignal, createEffect, untrack } from 'solid-js';
import { bridges, getConnection } from './store.ts';
import { OpbridgeError, type StreamDeckAction } from './httpClient.ts';

export type StreamDeckCatalogueStatus =
  /** Never fetched in this session. */
  | 'idle'
  /** Fetch in flight. */
  | 'loading'
  /** Fetched; `actions` is authoritative (possibly legitimately empty). */
  | 'ready'
  /** No connected primary OPBridge to ask. */
  | 'no-bridge'
  /** OPBridge answered, but the Stream Deck / MCP side is unreachable
   *  (503), or the endpoint doesn't exist on this daemon yet (404). */
  | 'unavailable'
  /** Anything else — auth, network, malformed. `error` carries detail. */
  | 'error';

const [actionsSig, setActions] = createSignal<StreamDeckAction[]>([]);
const [statusSig, setStatus] = createSignal<StreamDeckCatalogueStatus>('idle');
const [errorSig, setError] = createSignal<string>('');
const [fetchedAtSig, setFetchedAt] = createSignal(0);

export const streamDeckActions = actionsSig;
export const streamDeckStatus = statusSig;
export const streamDeckError = errorSig;
export const streamDeckFetchedAt = fetchedAtSig;

/** Look up a cached action by id — lets a face render a live title even
 *  when the item's cached `actionTitle` is stale. Returns undefined when
 *  the catalogue hasn't been fetched or the action has since been removed
 *  from the MCP Actions profile (a real state worth showing). */
export function findStreamDeckAction(id: string | undefined): StreamDeckAction | undefined {
  if (!id) return undefined;
  return actionsSig().find((a) => a.id === id);
}

/** Human label for an action, degrading through the fields the bridge may
 *  or may not send (it's v0.1.1 — treat every field but `id` as optional). */
export function streamDeckActionLabel(a: StreamDeckAction): string {
  return a.title?.trim() || a.name?.trim() || a.id.slice(0, 8);
}

/** Secondary line for the picker: what the action actually is. */
export function streamDeckActionSubtitle(a: StreamDeckAction): string {
  const parts = [a.name, a.description].filter((s): s is string => !!s && !!s.trim());
  // Drop a redundant "Website · Website" when name and title coincide.
  const uniq = [...new Set(parts.map((s) => s.trim()))];
  return uniq.join(' · ');
}

let inFlight: Promise<void> | null = null;

/** How long to wait before the single retry-after-timeout below. */
const RETRY_AFTER_TIMEOUT_MS = 2500;

async function attemptStreamDeckFetch(isRetry: boolean): Promise<void> {
  const all = bridges();
  const primary = all.find((cfg) => cfg.is_primary) ?? all[0];
  const conn = primary ? getConnection(primary.id) : null;
  if (!primary || !conn || conn.status() !== 'connected') {
    setStatus('no-bridge');
    setError('');
    return;
  }
  setStatus('loading');
  try {
    const res = await conn.httpClient().streamDeckActions();
    setActions(Array.isArray(res?.actions) ? res.actions.filter((a) => a && a.id) : []);
    setFetchedAt(Date.now());
    setStatus('ready');
    setError('');
  } catch (err) {
    // OPBridge spawns the MCP server lazily on first request — 5.9s cold,
    // 62ms warm (OPBridge S165) — decoupled from the request that triggered
    // it. httpClient.streamDeckActions() now carries its own 10s timeout
    // (was silently inheriting the connection's 1200ms health-poll default —
    // the actual cause of the "3s timeout" this comment used to blame, and
    // of the CP-078 regression that outlived that fix). This retry stays as
    // a second line of defense for a cold start that overruns even the 10s
    // budget — rare now, but cheaper to keep than to prove it can't happen.
    // Only retry on the timeout kind — a genuine 404/503/network failure is
    // real and should surface immediately, not be hidden behind an extra
    // 2.5s stall.
    if (!isRetry && err instanceof OpbridgeError && err.kind === 'timeout') {
      await new Promise<void>((resolve) => setTimeout(resolve, RETRY_AFTER_TIMEOUT_MS));
      return attemptStreamDeckFetch(true);
    }
    setActions([]);
    if (err instanceof OpbridgeError) {
      // 404 = this OPBridge predates the endpoint. 503 = endpoint exists
      // but the MCP server isn't up. Both mean "can't offer a list", and
      // both are the operator's to fix, so say so rather than showing an
      // empty picker that looks like "you have no actions".
      const notThere = err.httpStatus === 404 || err.httpStatus === 503;
      setStatus(notThere ? 'unavailable' : 'error');
      setError(err.httpStatus === 404
        ? 'This OPBridge has no /streamdeck/actions endpoint yet.'
        : err.httpStatus === 503
          ? 'OPBridge cannot reach the Stream Deck MCP server. Is `elgato-mcp-server --http` running?'
          : `${err.kind}: ${err.message}`);
    } else {
      setStatus('error');
      setError(String(err));
    }
  }
}

/**
 * Fetch the catalogue from the primary OPBridge (DEC-063 — primary only).
 * Idempotent while a fetch is in flight. Never throws.
 */
export function refreshStreamDeckActions(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = attemptStreamDeckFetch(false).finally(() => { inFlight = null; });
  return inFlight;
}

/** Fetch only if we've never successfully loaded — for picker-open. */
export function ensureStreamDeckActions(): void {
  if (statusSig() === 'ready' || statusSig() === 'loading') return;
  void refreshStreamDeckActions();
}

/**
 * Warm the path as soon as a bridge is connected, if (and only if) the
 * project actually contains a Stream Deck item.
 *
 * Solves two problems with one request, which is why it lives here rather
 * than in the Inspector:
 *
 *  1. **The connect race.** The Inspector fetches when its block first
 *     renders, but a bridge takes ~3s to reach `connected` from a cold app
 *     start. Open the Inspector inside that window and the catalogue lands
 *     on `no-bridge` and STAYS there — the operator sees "No OPBridge
 *     connected" against a bridge that connected a second later, and only a
 *     manual Refresh recovers. Observed on the real rig 2026-07-28.
 *
 *  2. **The 5.9s cold start.** OPBridge spawns `@elgato/mcp-server` lazily,
 *     on the first Stream Deck request: measured 5.9s cold, 62ms warm
 *     (OPBridge S165). OPBridge suggested warming when "the Inspector or
 *     page opens" — but the Inspector is an EDIT-mode surface, and at a gig
 *     the operator is in test/performance mode and never opens it. Warming there
 *     would leave the actual failure case untouched: first press of the set
 *     hanging ~6s. Warming on connect covers the gig path, because by the
 *     time anything is pressed the server has been up for minutes.
 *
 * The `hasStreamDeckItem` gate preserves OPBridge's deliberate property
 * that a rig which never touches Stream Deck spawns no Node process at all
 * — we only warm for projects that actually have one of these items.
 */
export function installStreamDeckPrewarm(
  isConnected: () => boolean,
  hasStreamDeckItem: () => boolean,
): void {
  createEffect(() => {
    if (!isConnected() || !hasStreamDeckItem()) return;
    // Re-fetch whenever we're not already holding a good catalogue. Covers
    // first connect, reconnect after a drop, and a bridge appearing later.
    const s = untrack(statusSig);
    if (s === 'ready' || s === 'loading') return;
    void refreshStreamDeckActions();
  });
}

/** Test seam: reset module state between smokes. */
export function __resetStreamDeckActions(): void {
  setActions([]);
  setStatus('idle');
  setError('');
  setFetchedAt(0);
  inFlight = null;
}
